import React from 'react';
import AuthService from './authService.js';
import dateTimeHelper from '../Helpers/datetimeHelper.js';
import TimeSheetEntry from '../Domain/TimeSheetEntry.js';
import TimesheetHelper from '../Helpers/ModelHelpers/TimesheetHelper.js';

class TimeSheetEntryService extends React.Component {
  static GetOutsourcedOfficeOptions(date) {
    const authService = AuthService.getInstance();
    return authService.CallBackendFunction(`/api/TimesheetEntry/GetOutsourcedOfficeOptions?date=${date}`);
  }

  // The billable offices are narrowed by whatever the entry is labelled with, so the whole labelling is sent.
  // Ids that are unset (or the -1 the editor uses for "none") are left out entirely rather than sent as nulls.
  static GetOutsourcedActivityOfficeOptions(labelling) {
    const authService = AuthService.getInstance();
    const query = Object.entries(labelling || {})
      .filter(([, id]) => id != null && id !== -1)
      .map(([name, id]) => `${name}=${id}`)
      .join('&');

    return authService.CallBackendFunction(`/api/TimesheetEntry/GetOutsourcedActivityOfficeOptions?${query}`);
  }

  static async GetTimeSheetEntries(selectedDate, userId = null) {
    userId = userId ? userId : AuthService.getInstance().GetLoggedInUser().userId;
    const authService = AuthService.getInstance();

    let url = '/api/TimesheetEntry/';
    url += `?userId=${userId}`;

    if (selectedDate) {
      const endDate = dateTimeHelper.addDaysToDate(selectedDate, 7);
      url += `&startDateTime=${dateTimeHelper.toYYYYsMMsDD(
        selectedDate,
        '-'
      )}&endDateTime=${dateTimeHelper.toYYYYsMMsDD(endDate, '-')}`;
    }

    // Get offset from UTC time
    const now = new Date();
    url += `&offsetMinutes=${now.getTimezoneOffset()}&clearCache=true`;
    return authService.CallBackendFunction(url);
  }

  static async GetWeeklyWorkingMinutes(userId, selectedDate) {
    const authService = AuthService.getInstance();

    let url = '/api/TimesheetEntry/GetWeeklyWorkingMinutes/';
    url += `?userId=${userId}`;

    if (selectedDate) {
      const endDate = dateTimeHelper.addDaysToDate(selectedDate, 7);
      url += `&startDateTime=${dateTimeHelper.toYYYYsMMsDD(
        selectedDate,
        '-'
      )}&endDateTime=${dateTimeHelper.toYYYYsMMsDD(endDate, '-')}`;
    }

    // Get offset from UTC time
    const now = new Date();
    url += `&offsetMinutes=${now.getTimezoneOffset()}&clearCache=false`;
    return authService.CallBackendFunction(url);
  }

  static GetTimeSheetEntriesSplitByDay(
    startDate,
    endDate,
    leadId,
    clientId,
    opportunityId,
    cfId,
    projectId,
    userId = null
  ) {
    const authService = AuthService.getInstance();
    userId = userId ? userId : authService.GetLoggedInUser().userId;
    let url = '/api/TimesheetEntry?splitByDays=true';

    url += `&userId=${userId}`;
    if (startDate) url += `&startDateTime=${dateTimeHelper.toYYYYsMMsDDsHHsMM(startDate)}`;
    if (endDate) url += `&endDateTime=${dateTimeHelper.toYYYYsMMsDDsHHsMM(endDate)}`;
    if (leadId && leadId !== -1) url += `&leadId=${leadId}`;
    if (clientId && clientId !== -1) url += `&clientId=${clientId}`;
    if (opportunityId && opportunityId !== -1) url += `&opportunityId=${opportunityId}`;
    if (cfId && cfId !== -1) url += `&cfId=${cfId}`;
    if (projectId && projectId !== -1) url += `&projectId=${projectId}`;

    // Get offset from UTC time
    const now = new Date();
    url += `&offsetMinutes=${now.getTimezoneOffset()}&userId=${userId}&clearCache=true`;
    return authService.CallBackendFunction(url);
  }

  static SaveTimesheetEntry(timesheetEntry) {
    const authService = AuthService.getInstance();
    timesheetEntry = TimesheetHelper.ConvertEntryDatesToUtc(timesheetEntry);
    return timesheetEntry.id === ''
      ? authService.CallBackendFunctionPost('/api/TimesheetEntry', this.ConvertTimesheetEntryToJSON(timesheetEntry))
      : authService.CallBackendFunctionPut(
          `/api/TimesheetEntry/${timesheetEntry.id}`,
          this.ConvertTimesheetEntryToJSON(timesheetEntry)
        );
  }

  static DeleteTimesheetEntry(timesheetEntry) {
    const authService = AuthService.getInstance();
    return authService.CallBackendFunctionDelete(`/api/TimesheetEntry/${timesheetEntry.id}`);
  }

  static ConvertTimesheetEntryToJSON(timesheetEntry) {
    const authService = AuthService.getInstance();
    return {
      startDate: timesheetEntry.startDate.split('.').join('-').concat('T00:00:00'),
      endDate: timesheetEntry.endDate.split('.').join('-').concat('T00:00:00'),
      startTime: '1900-01-01T'.concat(timesheetEntry.startTime),
      endTime: '1900-01-01T'.concat(timesheetEntry.endTime),
      // An existing entry keeps its owner's userId (HR/Admin may edit another user's PTO
      // entry, which must not re-own it); only brand-new entries default to the logged-in user.
      userId: timesheetEntry.userId ?? authService.GetTimesheetUser().userId,
      comment: timesheetEntry.comment !== undefined ? timesheetEntry.comment : '',
      taskId: timesheetEntry.taskId,
      leadId: timesheetEntry.leadId !== undefined && timesheetEntry.leadId !== -1 ? timesheetEntry.leadId : null,
      clientId:
        timesheetEntry.clientId !== undefined && timesheetEntry.clientId !== -1 ? timesheetEntry.clientId : null,
      opportunityId:
        timesheetEntry.opportunityId !== undefined && timesheetEntry.opportunityId !== -1
          ? timesheetEntry.opportunityId
          : null,
      clientFeeId: timesheetEntry.cfId !== undefined && timesheetEntry.cfId !== -1 ? timesheetEntry.cfId : null,
      projectId:
        timesheetEntry.projectId !== undefined && timesheetEntry.projectId !== -1 ? timesheetEntry.projectId : null,
      surveyId:
        timesheetEntry.surveyId !== undefined && timesheetEntry.surveyId !== -1 ? timesheetEntry.surveyId : null,
      tpSurveyId:
        timesheetEntry.tpSurveyId !== undefined && timesheetEntry.tpSurveyId !== -1 ? timesheetEntry.tpSurveyId : null,
      serviceType: timesheetEntry.serviceType,
      billedOfficeId: timesheetEntry.billedOfficeId || null,
      isOutsourced: timesheetEntry.isOutsourced?.toString() === 'true',
    };
  }

  static ConvertDBTimesheetEntries(timesheetEntries, isExport = false) {
    const convertedTimeSheetEntries = [];
    for (const timesheetEntry of timesheetEntries) {
      let newEntry = new TimeSheetEntry(
        timesheetEntry.startDate.replaceAll('-', '.').substring(0, 10), // +" "+TimesheetEntries[i].startTime.substring(11,19)+" (UTC)",
        timesheetEntry.endDate.replaceAll('-', '.').substring(0, 10), // +" "+TimesheetEntries[i].endTime.substring(11,19)+" (UTC)",
        timesheetEntry.startTime.substring(11, 16),
        timesheetEntry.endTime.substring(11, 16),
        timesheetEntry.task.name,
        timesheetEntry.comment
      );

      newEntry.id = timesheetEntry.timesheetEntryId;
      newEntry.taskId = timesheetEntry.task.taskId;
      newEntry.leadId = timesheetEntry.leadId;
      newEntry.leadName = timesheetEntry.lead ? timesheetEntry.lead.name : '';
      newEntry.clientId = timesheetEntry.clientId;
      newEntry.clientName = timesheetEntry.client ? timesheetEntry.client.name : '';
      newEntry.cfId = timesheetEntry.clientFeeId;
      newEntry.cfName = timesheetEntry.clientFee ? timesheetEntry.clientFee.cfName : '';
      newEntry.opportunityId = timesheetEntry.opportunityId;
      newEntry.opportunityName = timesheetEntry.opportunity ? timesheetEntry.opportunity.name : '';
      newEntry.surveyId = timesheetEntry.surveyId;
      newEntry.surveyName = timesheetEntry.survey ? timesheetEntry.survey.name : '';
      newEntry.tpSurveyId = timesheetEntry.tpSurveyId;
      newEntry.tpSurveyName = timesheetEntry.tpSurvey ? timesheetEntry.tpSurvey.thirdPartySurveyName : '';
      newEntry.projectId = timesheetEntry.projectId;
      newEntry.projectName = timesheetEntry.project ? timesheetEntry.project.projectName : '';
      newEntry.userId = timesheetEntry.userId;
      newEntry.freelancerLineItemId = timesheetEntry.freelancerLineItemId;
      newEntry.serviceType = timesheetEntry.serviceType;
      newEntry.jiraWorklogId = timesheetEntry.jiraWorklogId;
      newEntry.isBambooHrCreated = timesheetEntry.isBambooHrCreated ?? false;
      newEntry.billedOfficeId = timesheetEntry.billedOfficeId;
      newEntry.billedOfficeName = timesheetEntry.billedOffice ? timesheetEntry.billedOffice.name : '';
      newEntry.isOutsourced = timesheetEntry.isOutsourced ?? false;
      convertedTimeSheetEntries.push(newEntry);

      newEntry = TimesheetHelper.ConvertEntryDatesToLocal(newEntry);
      // newEntry.divId = `divEntry${newEntry.startDate.split(".").join("")}_${newEntry.startTime.split(":").join("")}`;
      newEntry.divId = `divEntry${newEntry.id}`;
      newEntry.text = `${newEntry.startTime}-${newEntry.endTime} ${newEntry.taskName}`;
      newEntry.processed = timesheetEntry.processed;

      if (
        newEntry.endDate > newEntry.startDate &&
        newEntry.startTime > newEntry.endTime &&
        newEntry.endTime !== '00:00' &&
        !isExport
      ) {
        // Set the displayed end time to midnight
        // This is the first part of the overnight entry, that is shown before midnight
        newEntry.displayEndTime = '00:00';
        newEntry.displayEndDate = newEntry.startDate;

        let newEntrySecondPart = new TimeSheetEntry(
          timesheetEntry.startDate.replaceAll('-', '.').substring(0, 10), // +" "+TimesheetEntries[i].startTime.substring(11,19)+" (UTC)",
          timesheetEntry.endDate.replaceAll('-', '.').substring(0, 10), // +" "+TimesheetEntries[i].endTime.substring(11,19)+" (UTC)",
          timesheetEntry.startTime.substring(11, 16),
          timesheetEntry.endTime.substring(11, 16),
          timesheetEntry.task.name,
          timesheetEntry.comment
        );
        newEntrySecondPart.id = timesheetEntry.timesheetEntryId;
        newEntrySecondPart.taskId = timesheetEntry.task.taskId;
        newEntrySecondPart.leadId = timesheetEntry.leadId;
        newEntrySecondPart.leadName = timesheetEntry.lead ? timesheetEntry.lead.name : '';
        newEntrySecondPart.clientId = timesheetEntry.clientId;
        newEntrySecondPart.clientName = timesheetEntry.client ? timesheetEntry.client.name : '';
        newEntrySecondPart.cfId = timesheetEntry.clientFeeId;
        newEntrySecondPart.cfName = timesheetEntry.clientFee ? timesheetEntry.clientFee.cfName : '';
        newEntrySecondPart.opportunityId = timesheetEntry.opportunityId;
        newEntrySecondPart.opportunityName = timesheetEntry.opportunity ? timesheetEntry.opportunity.name : '';
        newEntrySecondPart.surveyId = timesheetEntry.surveyId;
        newEntrySecondPart.surveyName = timesheetEntry.survey ? timesheetEntry.survey.name : '';
        newEntrySecondPart.tpSurveyId = timesheetEntry.tpSurveyId;
        newEntrySecondPart.tpSurveyName = timesheetEntry.tpSurvey ? timesheetEntry.tpSurvey.thirdPartySurveyName : '';
        newEntrySecondPart.projectId = timesheetEntry.projectId;
        newEntrySecondPart.projectName = timesheetEntry.project ? timesheetEntry.project.projectName : '';
        newEntrySecondPart.userId = timesheetEntry.userId;
        newEntrySecondPart.freelancerLineItemId = timesheetEntry.freelancerLineItemId;
        newEntrySecondPart.serviceType = timesheetEntry.serviceType;
        newEntrySecondPart.isBambooHrCreated = timesheetEntry.isBambooHrCreated ?? false;
        newEntrySecondPart.billedOfficeId = timesheetEntry.billedOfficeId;
        newEntrySecondPart.billedOfficeName = timesheetEntry.billedOffice ? timesheetEntry.billedOffice.name : '';
        newEntrySecondPart.isOutsourced = timesheetEntry.isOutsourced ?? false;
        convertedTimeSheetEntries.push(newEntrySecondPart);

        newEntrySecondPart = TimesheetHelper.ConvertEntryDatesToLocal(newEntrySecondPart);
        newEntrySecondPart.divId = `divEntry${newEntrySecondPart.startDate
          .split('.')
          .join('')}_${newEntrySecondPart.startTime.split(':').join('')}_part2`;
        newEntrySecondPart.text = `${newEntrySecondPart.startTime}-${newEntrySecondPart.endTime} ${newEntrySecondPart.taskName}`;
        newEntrySecondPart.processed = timesheetEntry.processed;

        // Set display dates for second part
        newEntrySecondPart.displayStartDate = newEntrySecondPart.endDate;
        newEntrySecondPart.displayStartTime = '00:00';

        const end = new Date(newEntry.displayEndDate);
        const start = new Date(newEntrySecondPart.displayStartDate);
        const days = TimesheetHelper.daysBetween(start, end);

        if (days >= 2) {
          // If it was actually multiday entry, so we have to change both parts' display dates
          const displayEndDate = new Date(newEntrySecondPart.endDate);
          const formattedDate = displayEndDate.toISOString().split('T')[0].replaceAll('-', '.');
          newEntrySecondPart.displayStartDate = formattedDate;
          newEntrySecondPart.displayStartTime = '00:00';

          const newDisplayEnd = new Date(newEntrySecondPart.endDate);
          newDisplayEnd.setDate(newDisplayEnd.getDate() + 1);
          newEntry.displayEndDate = newDisplayEnd.toISOString().split('T')[0].replaceAll('-', '.');

          const newDisplayStart = new Date(newEntry.tempStartDate);
          newDisplayStart.setDate(newDisplayStart.getDate() + 2);
          newEntrySecondPart.displayStartDate = newDisplayStart.toISOString().split('T')[0].replaceAll('-', '.');
        }
      }
    }
    return convertedTimeSheetEntries;
  }
}

export default TimeSheetEntryService;
